import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { initializePWA } from "@/components/PWAComponents";
import { registerServiceWorker, subscribeToPush } from "@/utils/pwa";

import SEOHead from '@/components/SEOHead';
import { installApiCache } from '@/lib/apiCache';

// Initialize PWA
initializePWA();

// Context
import { TrialProvider } from "@/context/TrialContext";

// Error Boundary for crash protection
import ErrorBoundary from "@/components/ErrorBoundary";

// Components
import TrialExpiredBanner from "@/components/TrialExpiredBanner";
import TrialExpiryPopup from "@/components/TrialExpiryPopup";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyPendingPage from "@/pages/VerifyPendingPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import Dashboard from "@/pages/Dashboard";
import AgentsPage from "@/pages/AgentsPage";
import ChatbotDetail from "@/pages/ChatbotDetail";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OverallAnalyticsPage from "@/pages/OverallAnalyticsPage";
import ChatbotAnalyticsPage from "@/pages/ChatbotAnalyticsPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import FeedbackPage from '@/pages/FeedbackPage';
import BlogDetailPage from "@/pages/BlogDetailPage";
import BlogPage from "@/pages/BlogPage";
import PricingPage from "@/pages/marketing/PricingPage";
import AiCustomerServicePage from "@/pages/marketing/AiCustomerServicePage";
import AiWhatsAppPage from "@/pages/marketing/AiWhatsAppPage";
import CrmOrdersPage from "@/pages/marketing/CrmOrdersPage";
import AboutPage from "@/pages/marketing/AboutPage";
import SettingsPage from "@/pages/SettingsPage";
import PaymentPage from "@/pages/PaymentPage";
import ChannelsPage from "@/pages/ChannelsPage";
import WhatsAppOnboarding from "@/pages/WhatsAppOnboarding";
import WhatsAppGuide from "@/pages/WhatsAppGuide";
import MessengerOnboarding from "@/pages/MessengerOnboarding";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InboxPage from "@/pages/InboxPage";
import BroadcastPage from "@/pages/BroadcastPage";
import ConversationDetailPage from "@/pages/ConversationDetailPage";
import CatalogPage from "@/pages/CatalogPage";
import DigitalPage from "@/pages/DigitalPage";
import DigitalDownloadPage from "@/pages/DigitalDownloadPage";
import SheetsPage from "@/pages/SheetsPage";
import OrdersPage from "@/pages/OrdersPage";
import CustomersPage from "@/pages/CustomersPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AIInsightsPage from "@/pages/AIInsightsPage";
import FinancialReportPage from "@/pages/FinancialReportPage";
import PromosPage from "@/pages/PromosPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Instant page navigation: serve repeat GETs from memory, refresh in background
installApiCache(axios);

// Auth Context
export const AuthContext = React.createContext(null);
export const useAuth = () => React.useContext(AuthContext);

// Theme Context
export const ThemeContext = React.createContext(null);
export const useTheme = () => React.useContext(ThemeContext);

// Language Context
export const LanguageContext = React.createContext(null);
export const useLanguage = () => React.useContext(LanguageContext);

// Protected Route Component with Onboarding Check
const ProtectedRoute = ({ children, skipOnboardingCheck = false }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Skip onboarding check if flag is set or already on onboarding/whatsapp-onboarding page
      const skipPaths = ['/onboarding', '/whatsapp-onboarding', '/messenger-setup'];
      if (skipOnboardingCheck || skipPaths.some(p => location.pathname.startsWith(p))) {
        setCheckingOnboarding(false);
        return;
      }

      // Check localStorage first for quick access
      const localCompleted = localStorage.getItem('onboarding_completed');
      if (localCompleted === 'true') {
        setOnboardingCompleted(true);
        setCheckingOnboarding(false);
        return;
      }

      // Check from backend
      const authToken = token || localStorage.getItem('token');
      if (authToken) {
        try {
          const response = await axios.get(`${API}/onboarding/status`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          if (response.data?.completed) {
            localStorage.setItem('onboarding_completed', 'true');
            setOnboardingCompleted(true);
          } else {
            setOnboardingCompleted(false);
          }
        } catch (error) {
          console.error('Failed to check onboarding status:', error);
          // On error, assume completed to not block existing users
          setOnboardingCompleted(true);
        }
      }
      setCheckingOnboarding(false);
    };

    if (!loading && user) {
      checkOnboardingStatus();
    } else if (!loading) {
      setCheckingOnboarding(false);
    }
  }, [loading, user, token, skipOnboardingCheck, location.pathname]);

  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not completed
  const skipPaths = ['/onboarding', '/whatsapp-onboarding', '/messenger-setup'];
  if (onboardingCompleted === false && !skipPaths.some(p => location.pathname.startsWith(p))) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { 
        headers,
        withCredentials: true,
        timeout: 10000
      });
      setUser(response.data);
    } catch (error) {
      console.log('Auth check failed, clearing session');
      setUser(null);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // GoogleAuthCallback will exchange the one-time code and establish the session first.
    if (window.location.hash?.includes('gcode=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  // Re-check auth when token changes
  useEffect(() => {
    if (token) {
      checkAuth();
    }
  }, [token]);

  // Auto-subscribe push notification when user is logged in and permission granted
  useEffect(() => {
    if (user && token && 'Notification' in window && Notification.permission === 'granted') {
      (async () => {
        try {
          const reg = await registerServiceWorker();
          if (reg) {
            await subscribeToPush(reg, user.id, token);
          }
        } catch (e) {
          // Silent - push subscription is optional
        }
      })();
    }
  }, [user, token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const exchangeGoogleCode = async (code) => {
    const response = await axios.post(`${API}/auth/google/exchange`, { code }, { withCredentials: true });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, exchangeGoogleCode, logout, token, checkAuth, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'id';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'id' ? 'en' : 'id');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Trial Banner Wrapper - Only shows banner on protected pages
const TrialBannerWrapper = ({ children }) => {
  const location = useLocation();
  
  // Don't show banner on public pages
  const publicPaths = ['/', '/login', '/register', '/verify-pending', '/verify-email', '/privacy-policy', '/privacy', '/terms-conditions', '/terms', '/blog', '/whatsapp-guide', '/nrv-panel/login', '/forgot-password', '/reset-password'];
  const isPublicPage = publicPaths.some(path => location.pathname === path);
  
  if (isPublicPage) {
    return children;
  }
  
  return (
    <>
      <TrialExpiredBanner />
      <TrialExpiryPopup />
      {children}
    </>
  );
};

// Menangkap fragment #gcode= (kode sekali-pakai Google OAuth) SEBELUM route mana pun render
const GoogleAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('gcode=')) {
    return <GoogleAuthCallback />;
  }
  return children;
};

// Reads admin_token at render time so it reflects the token saved during login
const AdminRoute = () => (
  localStorage.getItem('admin_token') ? <AdminDashboard /> : <AdminLogin />
);


// Reset scroll ke atas setiap pindah halaman (SPA)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <TrialProvider>
              <TrialBannerWrapper>
                <Toaster position="top-right" richColors />
                <GoogleAuthGate>
                <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/verify-pending" element={<VerifyPendingPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/harga" element={<PricingPage />} />
          <Route path="/ai-customer-service" element={<AiCustomerServicePage />} />
          <Route path="/ai-whatsapp" element={<AiWhatsAppPage />} />
          <Route path="/crm-pesanan" element={<CrmOrdersPage />} />
          <Route path="/tentang-kami" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/inbox" element={
            <ProtectedRoute>
              <InboxPage />
            </ProtectedRoute>
          } />
          <Route path="/inbox/:id" element={
            <ProtectedRoute>
              <ConversationDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/agents" element={
            <ProtectedRoute>
              <AgentsPage />
            </ProtectedRoute>
          } />
          <Route path="/agent/:id" element={
            <ProtectedRoute>
              <ChatbotDetail />
            </ProtectedRoute>
          } />
          <Route path="/broadcast" element={
            <ProtectedRoute>
              <BroadcastPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={<Navigate to="/financial" replace />} />
          <Route path="/analytics/:id" element={
            <ProtectedRoute>
              <ChatbotAnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/catalog" element={
            <ProtectedRoute>
              <CatalogPage />
            </ProtectedRoute>
          } />
          <Route path="/digital" element={
            <ProtectedRoute>
              <DigitalPage />
            </ProtectedRoute>
          } />
          <Route path="/digital/products" element={
            <ProtectedRoute>
              <CatalogPage variant="digital" />
            </ProtectedRoute>
          } />
          {/* Halaman unduh customer — PUBLIK, tanpa login */}
          <Route path="/d/:token" element={<DigitalDownloadPage />} />
          {/* Google Sheets - hidden, dalam pengembangan */}
          {false && <Route path="/sheets" element={
            <ProtectedRoute>
              <SheetsPage />
            </ProtectedRoute>
          } />}
          <Route path="/orders" element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          } />
          <Route path="/customers" element={
            <ProtectedRoute>
              <CustomersPage />
            </ProtectedRoute>
          } />
          {/* AI Insights Page - Full Implementation */}
          <Route path="/ai-insights" element={
            <ProtectedRoute>
              <AIInsightsPage />
            </ProtectedRoute>
          } />
          <Route path="/financial" element={
            <ProtectedRoute>
              <FinancialReportPage />
            </ProtectedRoute>
          } />
          <Route path="/feedback" element={
            <ProtectedRoute>
              <FeedbackPage />
            </ProtectedRoute>
          } />
          {/* <Route path="/promos" element={
            <ProtectedRoute>
              <PromosPage />
            </ProtectedRoute>
          } /> */}
          {/* Settings routes - redirect base /settings to /settings/profile */}
          <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/settings/profile" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/shipping" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/payment" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/notifications" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/follow-up" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          {/* Invoice route hidden - fitur dinonaktifkan sementara
          <Route path="/settings/invoice" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          */}
          <Route path="/settings/subscription" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          {/* Redirect old /profile to /settings/profile */}
          <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/payment" element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          } />
          <Route path="/channels" element={
            <ProtectedRoute>
              <ChannelsPage />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp-onboarding" element={
            <ProtectedRoute>
              <WhatsAppOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp-guide" element={
            <WhatsAppGuide />
          } />
          <Route path="/messenger-setup" element={
            <ProtectedRoute>
              <MessengerOnboarding />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/nrv-panel/login" element={<AdminLogin />} />
          <Route path="/nrv-panel" element={<AdminRoute />} />
          
          {/* Super Admin Route */}
          <Route path="/superadmin" element={<SuperAdminPage />} />
            </Routes>
                </GoogleAuthGate>
              </TrialBannerWrapper>
            </TrialProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
